import type React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { UnauthorizedState } from "@frontend/shared/presentation/components/feedback/UnauthorizedState";
import {
  UnderlineTabs,
  UnderlineTabsList,
  UnderlineTabsTrigger,
  UnderlineTabsContent,
} from "@planta-v2/pds-ui/underline-tabs";
import { BackButton } from "@planta-v2/pds-ui/back-button";
import { i18n } from "@frontend/i18n";
import { useEventThemeFromConfig } from "@/shared/presentation/hooks/useEventThemeFromConfig";
import { useUserAccountController } from "../controllers/useUserAccountController";
import { AccountSettingsTab } from "../components/AccountSettingsTab";
import { EmailVerificationFlow } from "../components/EmailVerificationFlow";
import { YourEventsTab } from "../components/YourEventsTab";

export function UserAccountPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const eventIdPublic = searchParams.get("event") ?? undefined;
  useEventThemeFromConfig(eventIdPublic);

  const { state, actions } = useUserAccountController();

  if (state.isUnauthorized) {
    return <UnauthorizedState onRetry={actions.refresh} />;
  }

  if (state.isAccessResolving) {
    return <></>;
  }

  const handleBack = (): void => {
    void navigate(-1);
  };

  const handleTabChange = (value: string): void => {
    if (value === "settings" || value === "events") {
      actions.setActiveTab(value);
    }
  };

  return (
    <div
      className="flex h-dvh flex-col bg-background"
      data-testid="user-account-page"
    >
      {/* Header */}
      <header className="flex shrink-0 items-center px-4 pt-4 pb-2 bg-background">
        <BackButton
          onClick={handleBack}
          label={i18n.t("user.account.header.back")}
          data-testid="user-account-back-button"
        />
      </header>

      {/* Title */}
      <div className="px-4 pb-2">
        <h1 className="page-title">{i18n.t("user.account.title")}</h1>
      </div>

      {/* Tabs */}
      <UnderlineTabs
        value={state.activeTab}
        onValueChange={handleTabChange}
        className="flex flex-1 flex-col min-h-0"
      >
        <UnderlineTabsList className="px-4">
          <UnderlineTabsTrigger
            value="settings"
            data-testid="user-account-settings-tab"
          >
            {i18n.t("user.account.tabs.settings")}
          </UnderlineTabsTrigger>
          <UnderlineTabsTrigger
            value="events"
            data-testid="user-account-events-tab"
          >
            {i18n.t("user.account.tabs.events")}
          </UnderlineTabsTrigger>
        </UnderlineTabsList>

        <UnderlineTabsContent
          value="settings"
          className="flex-1 overflow-y-auto min-h-0"
          data-testid="user-account-settings-content"
        >
          <div className="p-4">
            <AccountSettingsTab
              emailChannel={state.emailChannel}
              whatsappChannel={state.whatsappChannel}
              linkedInChannel={state.linkedInChannel}
              verificationSlot={
                state.emailChannel !== null &&
                !state.emailChannel.isVerified ? (
                  <EmailVerificationFlow />
                ) : undefined
              }
            />
          </div>
        </UnderlineTabsContent>

        <UnderlineTabsContent
          value="events"
          className="flex-1 overflow-y-auto min-h-0"
          data-testid="user-account-events-content"
        >
          <YourEventsTab />
        </UnderlineTabsContent>
      </UnderlineTabs>
    </div>
  );
}
